
import React, { useRef, useState, useCallback, useEffect } from 'react'
import classNames from 'classnames'
import PropTypes from 'prop-types'
import { useConfig } from '../ConfigProvider'
import cloneDeep from 'lodash/cloneDeep'
import FileItem from './FileItem'
import Button from '../Buttons'
import Notification from '../Notification'
import toast from '../toast'
import { HiOutlineTrash } from 'react-icons/hi'
import { GrDocumentCsv } from 'react-icons/gr'
import { OpenNotification } from 'views/Servicefile'
import { BsDownload } from 'react-icons/bs'

const filesToArray = (files) => Object.keys(files).map((key) => files[key])

const TicketUpload = React.forwardRef((props, ref) => {
    const {
        accept,
        beforeUpload,
        disabled,
        draggable,
        fileList,
        multiple,
        onChange,
        onFileRemove,
        showList,
        tip,
        uploadLimit,
        children,
        className,
        field,
        form,
        name,
        customIcon,
        showAcceptAsCsvOnly,
        ...rest
    } = props

    const fileInputField = useRef(null)
    const [files, setFiles] = useState(fileList)
    const [dragOver, setDragOver] = useState(false)

    const { themeColor, primaryColorLevel } = useConfig()

    useEffect(() => {
        setFiles(fileList)
    }, [fileList])

    const getAllowedExtensions = () => {
        return showAcceptAsCsvOnly
            ? ['.csv']
            : ['.pdf', '.doc', '.docx', '.jpeg', '.jpg',".png"]
    }


    const triggerMessage = (msg) => {
        toast.push(
            <Notification type={msg ? "success" : "danger"} duration={2000}>
                {msg || 'Upload Failed!'}
            </Notification>,
            { placement: 'top-center' }
        )
    }

    const pushFile = (newFiles, file) => {
        for (let f of newFiles) {
            file.push(f)
        }
        return file
    }

    const addNewFiles = (newFiles) => {
        let file = cloneDeep(files)
        if (typeof uploadLimit === 'number' && uploadLimit !== 0) {
            if (file.length >= uploadLimit) {
                if (uploadLimit === 1) {
                    file.shift()
                    file = pushFile(newFiles, file)
                }
                return filesToArray({ ...file })
            }
        }
        file = pushFile(newFiles, file)
        return filesToArray({ ...file })
    }

   const onNewFileUpload = async (e) => {
    const selectedFiles = Array.from(e.target.files || [])
    console.log(selectedFiles)
    const allowedExtensions = getAllowedExtensions();
    const MAX_SIZE = 5 * 1024 * 1024;

    const validNewFiles = [];

    for (const file of selectedFiles) {
        const fileName = file?.name?.toLowerCase() || '';
        const isValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));

        if (!isValidExtension) {
            OpenNotification('danger', `Invalid file type: ${file.name}`);
            continue;
        }

        if (file?.size > MAX_SIZE) {
            OpenNotification('danger', `${file.name} exceeds 5MB.`);
            continue;
        }

        const isDuplicate = files.some(f =>
            f.name === file.name &&
            f.size === file.size &&
            f.lastModified === file.lastModified
        );

        if (isDuplicate) {
            OpenNotification('danger', `${file.name} already uploaded.`);
            continue;
        }
        validNewFiles.push(file);
    }

    if (validNewFiles.length > 0) {
        const updatedFiles = [...files, ...validNewFiles];
        setFiles(updatedFiles);
        onChange({
            file: validNewFiles, 
            fileList: updatedFiles,
            originalFileList: files,
            success: true,
        });
    }

    e.target.value = null; 
};

    const removeFile = (fileIndex) => {
        const deletedFileList = files.filter((_, index) => index !== fileIndex)
        setFiles(deletedFileList)
         onFileRemove?.(deletedFileList)
    }

    const triggerUpload = (e) => {
        if (!disabled) {
            fileInputField.current?.click()
        }
        e.stopPropagation()
    }

    const renderChildren = () => {
        if (!draggable && !children) {
            return (
                <Button disabled={disabled} onClick={(e) => e.preventDefault()}>
                    Upload
                </Button>
            )
        }

        if (draggable && !children) {
            return (
                <div className='p-2'>
                    <p className='flex items-center pt-4'>
                        <span className="p-2" style={{ margin: "auto", backgroundColor: "#F0FDF4" }}>
                            {customIcon ? customIcon : <GrDocumentCsv size={30} style={{ color: "#15803D" }} />}
                        </span>
                    </p>
                    <p className='pt-2 pl-4'>
                        <span><span style={{ color: "#111827", fontWeight: "600" }}>Drop your file here or</span> <span style={{ color: "#2563EB", fontWeight: "600" }}>Browse</span></span>
                    </p>
                    <p className="p-4 text-center text-gray-400">
                        Support: {showAcceptAsCsvOnly ? 'csv' : accept ? accept.split(',').map(ext => ext.trim().replace('.', '')).join(', ') : 'N/A'}
                    </p>
                </div>
            )
        }

        return children
    }

    const handleDragLeave = useCallback(() => {
        if (draggable) setDragOver(false)
    }, [draggable])

    const handleDragOver = useCallback(() => {
        if (draggable && !disabled) setDragOver(true)
    }, [draggable, disabled])

    const handleDrop = useCallback(() => {
        if (draggable) setDragOver(false)
    }, [draggable])

    const draggableProp = {
        onDragLeave: handleDragLeave,
        onDragOver: handleDragOver,
        onDrop: handleDrop,
    }

    const draggableEventFeedbackClass = `border-${themeColor}-${primaryColorLevel}`

    const uploadClass = classNames(
        'upload',
        draggable && 'upload-draggable',
        draggable && !disabled && `hover:${draggableEventFeedbackClass}`,
        draggable && disabled && 'disabled',
        dragOver && draggableEventFeedbackClass,
        className
    )

    const uploadInputClass = classNames(
        'upload-input',
        draggable && 'draggable'
    )

    return (
        <>
            <div
                ref={ref}
                className={uploadClass}
                {...(draggable ? draggableProp : { onClick: triggerUpload })}
                {...rest}
            >
                <input
                    className={uploadInputClass}
                    type="file"
                    ref={fileInputField}
                    onChange={onNewFileUpload}
                    disabled={disabled}
                    multiple={multiple}
                    accept={accept}
                    title=""
                    {...field}
                    {...rest}
                    name={name}
                />
                {renderChildren()}
            </div>

            {tip}

            {showList && files.length > 0 && (
                <div className="upload-file-list border rounded-md mt-4 overflow-y-auto" style={{minHeight: '100px',maxHeight:'200px'}}>
                    {files.map((file, index) => (
                        <div className='flex items-center justify-between px-2 py-2' key={file.name + index}>
                            <FileItem file={file} />

                            <div className="flex items-center grid grid cols-5">
                                <p
                                    href={URL.createObjectURL(file)}
                                    download={file.name}
                                    className="text-blue-600 text-sm hover:underline col-span-4"
                                    title="Download"
                                >
                                    {/* <BsDownload size={20} /> */}
                                </p>

                                <div
                                    className='text-gray-600 cursor-pointer col-span-1'
                                    onClick={() => removeFile(index)}
                                    title="Remove"
                                >
                                    <HiOutlineTrash size={24} className='mr-2' />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </>
    )
})

TicketUpload.propTypes = {
    uploadLimit: PropTypes.number,
    draggable: PropTypes.bool,
    disabled: PropTypes.bool,
    showList: PropTypes.bool,
    multiple: PropTypes.bool,
    accept: PropTypes.string,
    customIcon: PropTypes.node,
    showAcceptAsCsvOnly: PropTypes.bool,
    tip: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
}

TicketUpload.defaultProps = {
    draggable: false,
    showAcceptAsCsvOnly: false,
    showList: true,
    disabled: false,
    customIcon: null,
    fileList: [],
}

export default TicketUpload
